import {
    PictureAsPdf as PictureAsPdfIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import {
    Alert,
    AppBar,
    Box,
    Button,
    Grid,
    Snackbar,
    Tab,
    Tabs,
    Toolbar,
    Typography,
} from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { useContext, useEffect, useRef, useState } from 'react';
import { ConfigContext } from '../../../base-ui/lib';
import { useApi } from '../../../hooks/useApi';

const sectionTitles = {
    coverPage: 'Cover Page',
    scopeOfWork: [
		'Scope of Work',
		{
			permitting: 'If Permitting',
			demoSquareFootage: 'If Demolition',
			dropBeam: 'If Drop Beam',
			main: 'Main',
			stairs: 'If Stairs',
			fascia: 'If Fascia',
		},
	],
    projectSpec: 'Project Specifications',
    paymentSchedule: 'Payment Schedule',
    signaturePage: 'Signature Page',
    termsAndConditions: 'Terms and Conditions',
};

// Define unique replacement variables for each section
const sectionReplacementVariables = {
    coverPage: ['{companyName}', '{companyAddress}', '{salesPersonName}', '{customerName}', '{customerAddress}'],
    scopeOfWork: {
        permitting: [],
        demoSquareFootage: [],
        dropBeam: [],
        main: ['{deckingType}'],
        stairs: [],
        fascia: [],
    },
    projectSpec: ['{permittingStatus}', '{deckingType}', '{railingType}', '{railCapStyle}'],
    paymentSchedule: [],
    signaturePage: ['{customerName}', '{customerAddress}', '{totalContractAmount}', '{depositAmount}'],
    termsAndConditions: ['{termsAndConditions}'],
};

// Aggregate all replacement variables for the Custom tab
const allReplacementVariables = Array.from(
	new Set(
		Object.keys(sectionReplacementVariables)
			.map((key) =>
				!Array.isArray(sectionReplacementVariables[key])
					? Object.values(sectionReplacementVariables[key]).flat()
					: sectionReplacementVariables[key]
			)
			.flat()
	)
);

const ProposalTemplateManager = () => {
    const { config } = useContext(ConfigContext);
    const api = useApi();

    const [tabIndex, setTabIndex] = useState(0);

    // Snackbar State for Notifications
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success', // 'error', 'warning', 'info'
    });

    // Default Tab State
    const [defaultSections, setDefaultSections] = useState({
        coverPage: '',
        scopeOfWork: {
            permitting: '',
            demoSquareFootage: '',
            dropBeam: '',
            main: '',
            stairs: '',
            fascia: '',
        },
        projectSpec: '',
        paymentSchedule: '',
        signaturePage: '',
        termsAndConditions: '',
    });

    const [defaultUsageCounts, setDefaultUsageCounts] = useState({
        coverPage: {},
        scopeOfWork: {
            permitting: {},
            demoSquareFootage: {},
            dropBeam: {},
            main: {},
            stairs: {},
            fascia: {},
        },
        projectSpec: {},
        paymentSchedule: {},
        signaturePage: {},
        termsAndConditions: {},
    });

    // Custom Tab State
    const [customContent, setCustomContent] = useState('');
    const [customUsageCounts, setCustomUsageCounts] = useState({});

    const [defaultTemplate, setDefaultTemplate] = useState('');

    // Editor References
    const editorRefs = {
        coverPage: useRef(null),
        scopeOfWork: {
            permitting: useRef(null),
            demoSquareFootage: useRef(null),
            dropBeam: useRef(null),
            main: useRef(null),
            stairs: useRef(null),
            fascia: useRef(null),
        },
        projectSpec: useRef(null),
        paymentSchedule: useRef(null),
        signaturePage: useRef(null),
        termsAndConditions: useRef(null),
        custom: useRef(null),
    };

    // Handle Tab Change
    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    // Handle setting the current template as the default template
    const handleSetAsDefault = async () => {
        try {
            await api.proposalTemplate.activate(tabIndex === 0 ? 'default' : 'custom');
            setDefaultTemplate(tabIndex === 0 ? 'default' : 'custom');
            setSnackbar({
                open: true,
                message: tabIndex === 0 ? 'Default template set as default successfully!' : 'Custom template set as default successfully!',
                severity: 'success',
            });
        } catch (error) {
            console.error('Error setting template as default:', error);
            setSnackbar({
                open: true,
                message: 'Failed to set template as default.',
                severity: 'error',
            });
        }
    };

    // Handle Save Template for Active Template
    const handleSave = async () => {
        try {
            const sectionTitleToDataMap = Object.entries(defaultSections).map(
				([section, data]) => {
					if (typeof sectionTitles[section] === 'string') {
						return [sectionTitles[section], data];
					}

					const subSectionsMapping = Object.entries(data).map(
						([subSection, subData]) => [
							sectionTitles[section][1][subSection],
							subData,
						]
					);
					return [
						sectionTitles[section][0],
						Object.fromEntries(subSectionsMapping),
					];
				}
			);
            const payload =
				tabIndex === 0
					? { default: Object.fromEntries(sectionTitleToDataMap) }
					: { custom: customContent };
            await api.proposalTemplate.save(payload);
            setSnackbar({
                open: true,
                message: tabIndex === 0 ? 'Default template saved successfully!' : 'Custom template saved successfully!',
                severity: 'success',
            });
        } catch (error) {
            console.error('Error saving templates:', error);
            setSnackbar({
                open: true,
                message: 'Failed to save templates.',
                severity: 'error',
            });
        }
    };

    // Handle Preview PDF for Active Template
    const handlePreviewPDF = async () => {
        try {
            const response = await api.proposalTemplate.preview(tabIndex === 0 ? 'default' : 'custom');
            const blob = await fetch(response).then((res) => res.blob());
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download =
				tabIndex === 0 ? 'proposal-tpl.default.pdf' : 'proposal-tpl.custom.pdf';
            document.body.appendChild(a).click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            setSnackbar({
                open: true,
                message: 'PDF generated successfully!',
                severity: 'success',
            });
        } catch (error) {
            console.error('Error generating PDF:', error);
            setSnackbar({
                open: true,
                message: 'Failed to generate PDF.',
                severity: 'error',
            });
        }
    };

    // Utility to count replacement variables
    const countVariables = (content, variables) => {
        const counts = {};
        variables.forEach((variable) => {
            const regex = new RegExp(variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'); // Escape regex characters
            const matches = content.match(regex);
            counts[variable] = matches ? matches.length : 0;
        });
        return counts;
    };

    // Handle Editor Change for Default Sections
    const handleDefaultEditorChange = (value, section, subSection) => {
        setDefaultSections((prev) => {
			if (typeof prev[section] === 'string') {
				return { ...prev, [section]: value };
			}

			return {
				...prev,
				[section]: { ...prev[section], [subSection]: value },
			};
		});
        setDefaultUsageCounts((prev) => {
			if (typeof prev[section] === 'string') {
				return { ...prev, [section]: countVariables(value, sectionReplacementVariables[section]) };
			}

			const subSectionUsageCounts = Object.entries(sectionReplacementVariables[section]).map(
				([subSection, variables]) => [subSection, countVariables(value, variables)]
			);
			return {
				...prev,
				[section]: Object.fromEntries(subSectionUsageCounts),
			};
		});
    };

    // Handle Editor Change for Custom
    const handleCustomEditorChange = (value) => {
        setCustomContent(value);
        setCustomUsageCounts(countVariables(value, allReplacementVariables));
    };

    useEffect(() => {
        (async () => {
            try {
                // Fetch templates from API
                const response = await api.proposalTemplate.get();
                const sectionTitleResponses = Object.entries(sectionTitles).map(([section, title]) => {
					if (typeof title === 'string') {
						return [section, response.default[title]];
					}

					const subSectionsMapping = Object.entries(title[1]).map(([subSection, subSectionTitle]) => [
						subSection,
						response.default[title[0]][subSectionTitle],
					]);
					return [section, Object.fromEntries(subSectionsMapping)];
				});
				
                setDefaultSections(Object.fromEntries(sectionTitleResponses));
                setCustomContent(response.custom);
                setDefaultTemplate(response.proposalDefault);
            } catch (error) {
                console.error('Error fetching templates:', error);
            }
        })();
    }, []);

    useEffect(() => {
        // Initialize Usage Counts on Load
        const initialCounts = {};
        Object.keys(defaultSections).forEach((section) => {
            if (typeof defaultSections[section] === 'object') {
                initialCounts[section] ??= {};
                return Object.keys(defaultSections[section]).forEach((subSection) => {
                    initialCounts[section][subSection] = countVariables(defaultSections[section][subSection], sectionReplacementVariables[section][subSection]);
                });
            }
            initialCounts[section] = countVariables(defaultSections[section], sectionReplacementVariables[section]);
        });
        setDefaultUsageCounts(initialCounts);
        if (customContent) setCustomUsageCounts(countVariables(customContent, allReplacementVariables));
    }, [defaultSections, customContent]);

    // Handle Snackbar Close
    const handleSnackbarClose = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* AppBar with Tabs */}
            <AppBar position="static" color="default">
                <Toolbar>
                    <Tabs value={tabIndex} onChange={handleTabChange} aria-label="proposal template tabs">
                        <Tab label="Default" />
                        <Tab label="Custom" />
                    </Tabs>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        variant="contained"
                        color='primary'
                        onClick={handleSetAsDefault}
                        disabled={defaultTemplate === (tabIndex === 0 ? 'default' : 'custom')}
                    >
                        Set as Default
                    </Button>
                </Toolbar>
            </AppBar>

            {/* Tab Panels */}
            <TabPanel value={tabIndex} index={0}>
                <Grid container spacing={4} sx={{ padding: 2 }}>
                    {/* Cover Page */}
                    <Grid item xs={12}>
                        <SectionEditor
                            title={sectionTitles.coverPage}
                            content={defaultSections.coverPage}
                            onChange={(value) => handleDefaultEditorChange(value, 'coverPage')}
                            usageCounts={defaultUsageCounts.coverPage}
                            replacementVariables={sectionReplacementVariables.coverPage}
                            editorRef={editorRefs.coverPage}
                            apiKey={config.emailEditor.apiKey}
                        />
                    </Grid>

                    {/* Scope of Work */}
                    <Grid item xs={12}>
                        <SectionEditor
                            title={sectionTitles.scopeOfWork}
                            content={defaultSections.scopeOfWork}
                            onChange={handleDefaultEditorChange}
                            usageCounts={defaultUsageCounts.scopeOfWork}
                            replacementVariables={sectionReplacementVariables.scopeOfWork}
                            editorRef={editorRefs.scopeOfWork}
                            apiKey={config.emailEditor.apiKey}
                        />
                    </Grid>

                    {/* Project Spec / Detail Page */}
                    <Grid item xs={12}>
                        <SectionEditor
                            title={sectionTitles.projectSpec}
                            content={defaultSections.projectSpec}
                            onChange={(value) => handleDefaultEditorChange(value, 'projectSpec')}
                            usageCounts={defaultUsageCounts.projectSpec}
                            replacementVariables={sectionReplacementVariables.projectSpec}
                            editorRef={editorRefs.projectSpec}
                            apiKey={config.emailEditor.apiKey}
                        />
                    </Grid>

                    {/* Payment Schedule */}
                    <Grid item xs={12}>
                        <SectionEditor
                            title={sectionTitles.paymentSchedule}
                            content={defaultSections.paymentSchedule}
                            onChange={(value) => handleDefaultEditorChange(value, 'paymentSchedule')}
                            usageCounts={defaultUsageCounts.paymentSchedule}
                            replacementVariables={sectionReplacementVariables.paymentSchedule}
                            editorRef={editorRefs.paymentSchedule}
                            apiKey={config.emailEditor.apiKey}
                        />
                    </Grid>

                    {/* Signature Page */}
                    <Grid item xs={12}>
                        <SectionEditor
                            title={sectionTitles.signaturePage}
                            content={defaultSections.signaturePage}
                            onChange={(value) => handleDefaultEditorChange(value, 'signaturePage')}
                            usageCounts={defaultUsageCounts.signaturePage}
                            replacementVariables={sectionReplacementVariables.signaturePage}
                            editorRef={editorRefs.signaturePage}
                            apiKey={config.emailEditor.apiKey}
                        />
                    </Grid>

                    {/* Terms and Conditions */}
                    <Grid item xs={12}>
                        <SectionEditor
                            title={sectionTitles.termsAndConditions}
                            content={defaultSections.termsAndConditions}
                            onChange={(value) => handleDefaultEditorChange(value, 'termsAndConditions')}
                            usageCounts={defaultUsageCounts.termsAndConditions}
                            replacementVariables={sectionReplacementVariables.termsAndConditions}
                            editorRef={editorRefs.termsAndConditions}
                            apiKey={config.emailEditor.apiKey}
                        />
                    </Grid>

                    {/* Save and Generate Buttons */}
                    {renderProposalTemplateActions(
						handleSave,
						handlePreviewPDF
					)}
                </Grid>
            </TabPanel>

            <TabPanel value={tabIndex} index={1}>
                <Grid container spacing={4} sx={{ padding: 2 }}>
                    {/* Replacement Variables Display */}
                    <Grid item xs={12}>
                        <Box component={'span'} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle1" sx={{fontWeight: 'bold'}}>Replacement Variables:</Typography>
                            {allReplacementVariables.map((variable) => (
                                <Typography key={variable} variant="body1">
                                    {variable}: {customUsageCounts[variable] || 0}
                                </Typography>
                            ))}
                        </Box>
                    </Grid>

                    {/* Custom Editor */}
                    <Grid item xs={12}>
                        <Editor
                            apiKey={config.emailEditor.apiKey}
                            onInit={(evt, editor) => (editorRefs.custom.current = editor)}
                            value={customContent}
                            onEditorChange={handleCustomEditorChange}
                            init={{
                                height: 750,
                                menubar: false,
                                plugins: [
                                    'advlist',
                                    'autolink',
                                    'lists',
                                    'link',
                                    'image',
                                    'charmap',
                                    'preview',
                                    'anchor',
                                    'searchreplace',
                                    'visualblocks',
                                    'code',
                                    'fullscreen',
                                    'insertdatetime',
                                    'table',
                                    'help',
                                    'wordcount',
                                    'pagebreak',
                                ],
                                toolbar:
                                    'undo redo | blocks fontfamily fontsize | ' +
                                    'bold italic forecolor backcolor | alignleft aligncenter ' +
                                    'alignright alignjustify | bullist numlist outdent indent | ' +
                                    'image link table pagebreak | code preview | removeformat | help',
                                content_style:
                                    'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                                relative_urls: false,
                                remove_script_host: false,
                                convert_urls: false,
                            }}
                        />
                    </Grid>

                    {/* Generate Button */}
                    {renderProposalTemplateActions(
						handleSave,
						handlePreviewPDF
					)}
                </Grid>
            </TabPanel>

            {/* Snackbar for Notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

// TabPanel Component
const TabPanel = (props) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`proposal-tabpanel-${index}`}
            aria-labelledby={`proposal-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
};

// SectionEditor Component
const SectionEditor = ({
    title,
    titleFormat = 'h4',
    content,
    onChange,
    usageCounts,
    replacementVariables,
    editorRef,
    apiKey,
}) => {
    if (title === sectionTitles.scopeOfWork)
        return (
            <Box>
                <Typography variant="h4" gutterBottom>
                    {title[0]}
                </Typography>
                {Object.keys(content).map((section, i, sections) => (
                    <Box key={section} sx={{ marginBottom: i === sections.length - 1 ? 0 : 4 }}>
                        <SectionEditor
                            title={title[1][section]}
                            titleFormat="h5"
                            content={content[section]}
                            onChange={(value) => onChange(value, 'scopeOfWork', section)}
                            usageCounts={usageCounts[section]}
                            replacementVariables={replacementVariables[section]}
                            editorRef={editorRef[section]}
                            apiKey={apiKey}
                        />
                    </Box>
                ))}
            </Box>
        );
    return (
        <Box>
            <Typography variant={titleFormat} gutterBottom>
                {title}
            </Typography>
            {(replacementVariables && replacementVariables.length > 0) && (
                <Box sx={{ marginBottom: 1 }}>
                    <Box component={'span'} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontWeight: 'bold' }} variant="subtitle1">
                            Replacement Variables:
                        </Typography>
                        {replacementVariables.map((variable) => (
                            <Typography key={variable} variant="body1">
                                {variable}: {usageCounts[variable] || 0}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            )}
            <Editor
                apiKey={apiKey}
                onInit={(evt, editor) => (editorRef.current = editor)}
                value={content}
                onEditorChange={(value) => onChange(value)}
                init={{
                    height: 400,
                    menubar: false,
                    plugins: [
                        'advlist',
                        'autolink',
                        'lists',
                        'link',
                        'image',
                        'charmap',
                        'preview',
                        'anchor',
                        'searchreplace',
                        'visualblocks',
                        'code',
                        'fullscreen',
                        'insertdatetime',
                        'table',
                        'help',
                        'wordcount',
                        'pagebreak',
                    ],
                    toolbar:
                        'undo redo | blocks fontfamily fontsize | ' +
                        'bold italic forecolor backcolor | alignleft aligncenter ' +
                        'alignright alignjustify | bullist numlist outdent indent | ' +
                        'image link table pagebreak | code preview | removeformat | help',
                    content_style:
                        'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                    relative_urls: false,
                    remove_script_host: false,
                    convert_urls: false,
                }}
            />
        </Box>
    );
};

const renderProposalTemplateActions = (handleSave, handlePreviewPDF) => {
	return (
		<Grid
			item
			xs={12}
			sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}
		>
			<Button
				variant="contained"
				color="primary"
				startIcon={<SaveIcon />}
				onClick={handleSave}
			>
				Save
			</Button>
			<Button
				variant="contained"
				color="secondary"
				startIcon={<PictureAsPdfIcon />}
				onClick={handlePreviewPDF}
			>
				Preview Template
			</Button>
		</Grid>
	);
};

export default ProposalTemplateManager;

